import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Plus, Trash2, Edit3, Filter } from "lucide-react";

export const DAMAGE_STATUSES = [
  { value: "open", label: "Deschis", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "in_progress", label: "În curs", color: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "insurance", label: "Asigurare", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "paid_by_employee", label: "Plătit angajat", color: "bg-purple-100 text-purple-800 border-purple-300" },
  { value: "written_off", label: "Pierdere", color: "bg-zinc-200 text-zinc-700 border-zinc-400" },
];

export const DAMAGE_SEVERITIES = [
  { value: "minor", label: "🟢 Minoră", color: "bg-emerald-100 text-emerald-800" },
  { value: "medium", label: "🟡 Medie", color: "bg-amber-100 text-amber-800" },
  { value: "major", label: "🔴 Majoră", color: "bg-red-100 text-red-800" },
];

function suggestSeverity(amount) {
  const a = Number(amount) || 0;
  if (a >= 1000) return "major";
  if (a >= 200) return "medium";
  return "minor";
}

const emptyForm = {
  date: new Date().toISOString().slice(0, 10),
  description: "",
  site_label: "",
  project_id: null,
  regie_id: null,
  amount_estimated: "",
  culprit_person_id: null,
  severity: "minor",
  status: "open",
  notes: "",
};

/**
 * Props:
 *  - lockedPersonId    : dacă e setat, daunele afișate sunt doar pentru acest angajat și
 *                        dialogul de adăugare îl pre-selectează (nu permite schimbare).
 *  - showFilters       : true pentru pagina globală
 *  - title             : header
 */
export default function DamagesPane({ lockedPersonId = null, showFilters = false, title = "Registru daune" }) {
  const { canDo, isAdmin } = useAuth();
  const canEdit = canDo("PERSONAL", "edit") || isAdmin;
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [persons, setPersons] = useState([]);
  const [projects, setProjects] = useState([]);
  const [regii, setRegii] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", severity: "", person_id: "", project_id: "" });
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null); // damage doc being edited
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (lockedPersonId) params.person_id = lockedPersonId;
      else if (filters.person_id) params.person_id = filters.person_id;
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      if (filters.project_id) params.project_id = filters.project_id;
      const [r, p, pr, rg] = await Promise.all([
        api.get("/damages", { params }),
        api.get("/persons-overview").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/regii").catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      setPersons(p.data || []);
      setProjects(pr.data || []);
      setRegii(rg.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lockedPersonId, filters.status, filters.severity, filters.person_id, filters.project_id]);

  const personOptions = useMemo(() => {
    return (persons || [])
      .filter((p) => p.active !== false)
      .map((p) => ({ id: p.id, label: `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)" }))
      .sort((a, b) => a.label.localeCompare(b.label, "ro"));
  }, [persons]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, culprit_person_id: lockedPersonId || null });
    setOpenDialog(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      date: d.date || "",
      description: d.description || "",
      site_label: d.site_label || "",
      project_id: d.project_id || null,
      regie_id: d.regie_id || null,
      amount_estimated: d.amount_estimated ?? "",
      culprit_person_id: d.culprit_person_id || null,
      severity: d.severity || "minor",
      status: d.status || "open",
      notes: d.notes || "",
    });
    setOpenDialog(true);
  };

  const save = async () => {
    if (!form.date || !form.description?.trim()) { toast.error("Dată și descriere obligatorii"); return; }
    const body = {
      date: form.date,
      description: form.description.trim(),
      site_label: form.site_label?.trim() || null,
      project_id: form.project_id || null,
      regie_id: form.regie_id || null,
      amount_estimated: Number(form.amount_estimated) || 0,
      culprit_person_id: form.culprit_person_id || null,
      severity: form.severity || "minor",
      status: form.status || "open",
      notes: form.notes?.trim() || null,
    };
    try {
      if (editing) {
        await api.put(`/damages/${editing.id}`, body);
        toast.success("Daună actualizată");
      } else {
        await api.post("/damages", body);
        toast.success("Daună înregistrată");
      }
      setOpenDialog(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    }
  };

  const onDelete = async (d) => {
    if (!(await confirm({ body: `Ștergi această daună (${fmtRoDate(d.date)}, ${d.amount_estimated} CHF)?`, danger: true }))) return;
    try {
      await api.delete(`/damages/${d.id}`);
      toast.success("Daună ștearsă");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare"); }
  };

  // Totaluri pentru afișare
  const totals = useMemo(() => {
    const t = rows.reduce((acc, r) => acc + (Number(r.amount_estimated) || 0), 0);
    const open = rows.filter((r) => ["open", "in_progress"].includes(r.status)).length;
    return { total: t, open, count: rows.length };
  }, [rows]);

  const getSiteLabel = (d) => {
    if (d.project_name) return `📋 ${d.project_name}`;
    if (d.regie_name) return `🛠 ${d.regie_name}`;
    if (d.site_label) return `📍 ${d.site_label}`;
    return "—";
  };

  return (
    <div className="space-y-4" data-testid="damages-pane">
      {/* Summary header */}
      <div className="card-flat p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm text-zinc-500">{title}</div>
          <div className="text-2xl font-display font-bold mt-1">
            {totals.count} daune <span className="text-zinc-400 text-base font-normal">· total {totals.total.toLocaleString("ro-RO", { maximumFractionDigits: 0 })} CHF</span>
          </div>
          {totals.open > 0 && (
            <div className="text-xs text-red-700 mt-1 inline-flex items-center gap-1 font-semibold">
              <AlertTriangle size={14}/> {totals.open} deschise/în curs
            </div>
          )}
        </div>
        {canEdit && (
          <Button onClick={openAdd} data-testid="damage-add-btn">
            <Plus size={16} className="mr-1" /> Adaugă daună
          </Button>
        )}
      </div>

      {/* Filters (global page only) */}
      {showFilters && (
        <div className="card-flat p-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 text-zinc-500"><Filter size={12}/> Filtre:</span>
          <Select value={filters.status || "__all__"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 w-[140px]" data-testid="filter-status"><SelectValue placeholder="Toate" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Toate statusurile</SelectItem>
              {DAMAGE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.severity || "__all__"} onValueChange={(v) => setFilters((f) => ({ ...f, severity: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 w-[120px]" data-testid="filter-severity"><SelectValue placeholder="Severitate" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Orice severitate</SelectItem>
              {DAMAGE_SEVERITIES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.person_id || "__all__"} onValueChange={(v) => setFilters((f) => ({ ...f, person_id: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 w-[200px]" data-testid="filter-person"><SelectValue placeholder="Vinovat" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Orice angajat</SelectItem>
              {personOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filters.status || filters.severity || filters.person_id) && (
            <Button size="sm" variant="ghost" onClick={() => setFilters({ status: "", severity: "", person_id: "", project_id: "" })}>Reset</Button>
          )}
        </div>
      )}

      {/* List */}
      <div className="card-flat p-0 overflow-hidden">
        {loading && <div className="p-4 text-sm text-zinc-500">Se încarcă…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-sm text-zinc-500 text-center" data-testid="damages-empty">
            Nicio daună înregistrată.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="divide-y divide-[var(--border)]">
            {rows.map((d) => {
              const sev = DAMAGE_SEVERITIES.find((s) => s.value === d.severity) || DAMAGE_SEVERITIES[0];
              const st = DAMAGE_STATUSES.find((s) => s.value === d.status) || DAMAGE_STATUSES[0];
              return (
                <div key={d.id} className="p-3 flex flex-wrap items-start gap-3 hover:bg-zinc-50/60" data-testid={`damage-row-${d.id}`}>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-bold">{(d.date || "").slice(0,4)}</span>
                      <span className="font-display font-semibold">{fmtRoDate(d.date)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sev.color}`}>{sev.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${st.color}`}>{st.label}</span>
                      <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 text-xs font-bold ml-auto">{Number(d.amount_estimated || 0).toLocaleString("ro-RO")} CHF</span>
                    </div>
                    <div className="text-sm font-medium mt-1">{d.description}</div>
                    <div className="text-xs text-zinc-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{getSiteLabel(d)}</span>
                      {d.culprit_person_name && <span className="font-medium">👤 {d.culprit_person_name}</span>}
                    </div>
                    {d.notes && <div className="text-xs text-zinc-600 mt-1">📝 {d.notes}</div>}
                    {d.created_at && (
                      <div className="text-[10px] text-zinc-400 mt-1">
                        Înregistrat de {d.created_by || "—"} la {fmtRoDate(d.created_at.slice(0, 10))}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} className="text-blue-600 hover:text-blue-700" data-testid={`damage-edit-${d.id}`} title="Editează">
                        <Edit3 size={14}/>
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => onDelete(d)} className="text-red-600 hover:text-red-700" data-testid={`damage-delete-${d.id}`} title="Șterge">
                          <Trash2 size={14}/>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl" data-testid="damage-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editează daună" : "Adaugă daună"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Data<span className="text-red-600">*</span></Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="damage-date" />
            </div>
            <div>
              <Label>Sumă estimată (CHF)</Label>
              <Input type="number" step={10} min={0} value={form.amount_estimated} onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, amount_estimated: v, severity: suggestSeverity(v) }));
              }} data-testid="damage-amount" />
              <p className="text-[10px] text-zinc-500 mt-0.5">Severitatea se actualizează automat după sumă (poți modifica manual).</p>
            </div>
            <div className="md:col-span-2">
              <Label>Descriere<span className="text-red-600">*</span></Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="ex: Geam spart cu macaraua la magazia 3" data-testid="damage-description" />
            </div>
            <div>
              <Label>Vinovat (angajat)</Label>
              <Select value={form.culprit_person_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, culprit_person_id: v === "__none__" ? null : v }))} disabled={!!lockedPersonId}>
                <SelectTrigger data-testid="damage-culprit"><SelectValue placeholder="Selectează angajatul" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Necunoscut —</SelectItem>
                  {personOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Șantier / Proiect</Label>
              <Select value={form.project_id || form.regie_id || "__none__"} onValueChange={(v) => {
                if (v === "__none__") setForm((f) => ({ ...f, project_id: null, regie_id: null }));
                else if (v.startsWith("p:")) setForm((f) => ({ ...f, project_id: v.slice(2), regie_id: null }));
                else if (v.startsWith("r:")) setForm((f) => ({ ...f, regie_id: v.slice(2), project_id: null }));
              }}>
                <SelectTrigger data-testid="damage-project"><SelectValue placeholder="Selectează" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— Niciunul (folosește textul liber) —</SelectItem>
                  {projects.length > 0 && <SelectItem disabled value="__h_proj__">— Proiecte —</SelectItem>}
                  {projects.map((p) => <SelectItem key={p.id} value={`p:${p.id}`}>📋 {p.name}</SelectItem>)}
                  {regii.length > 0 && <SelectItem disabled value="__h_reg__">— Regii —</SelectItem>}
                  {regii.map((r) => <SelectItem key={r.id} value={`r:${r.id}`}>🛠 {r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Sau text liber șantier</Label>
              <Input value={form.site_label} onChange={(e) => setForm((f) => ({ ...f, site_label: e.target.value }))} placeholder="ex: Implenia Basserdorf" data-testid="damage-site-label" />
            </div>
            <div>
              <Label>Severitate</Label>
              <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                <SelectTrigger data-testid="damage-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAMAGE_SEVERITIES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="damage-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAMAGE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Notă (opțional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="ex: foto raport, deviz, asigurător contactat..." data-testid="damage-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Anulează</Button>
            <Button onClick={save} data-testid="damage-save">{editing ? "Salvează" : "Adaugă"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
